import { useEffect, useRef, useState } from "react";
import { whenDisplayFontReady, WM_READY } from "../motion/reveals";
import ShineMark from "./ShineMark";
import { lockScroll, unlockScroll } from "../lib/scrollLock";
import { FILM_BLOCKED } from "../hooks/useAutoplayFilm";

/**
 * Branded page loader: black screen, the Grid Masters shield held static with
 * a ray of light sweeping through it (client, 2026-09-19).
 *
 * Rule (client, 2026-10-21): "if there is any delay in loading, the loader
 * shows; if the page doesn't need it, it shouldn't." So the loader is driven
 * by READINESS, not by a session flag:
 *
 *  - Gates: the display fonts (deterministic wordmark FLIP measurement), the
 *    hero + first-viewport images actually decoded (the page loads in full up
 *    front - nothing is lazy - and the loader covers that load), the hero
 *    wordmark scrub actually built (body[data-wm-state] has left "boot"), and
 *    on a FILM hero the film actually playing (`gm:film-pending` / `gm:film-playing`).
 *  - If every gate clears within GRACE_MS the loader never appears (warm
 *    cache, fast line). Otherwise it appears and stays until the gates clear,
 *    never for less than MIN_SHOW_MS (no flash), never longer than the cap.
 *
 * A film hero reached by client-side navigation re-raises the loader until
 * its film plays.
 */
export const FILM_PENDING = "gm:film-pending";
export const FILM_PLAYING = "gm:film-playing";
export const LOADER_DONE = "gm:loader-done"; // site revealed; below-the-fold media may start fetching
const FILM_CAP_MS = 12000;
// Fonts + images never hold the site longer than this. Was 9s; on a slow line
// the full-resolution hero frames (nothing is lazy - client, 2026-10-21) took
// longer and the site was revealed onto a black hero. The shield now holds for
// as long as the first viewport really needs (client: "the loader must stay
// until everything is ready"); this is only a safety net for a hung request.
const MEDIA_CAP_MS = 20000;
const WM_CAP_MS = 4000;     // the wordmark scrub build never holds longer than this
const GRACE_MS = 150;       // ready inside this window -> no loader at all
const MIN_SHOW_MS = 700;    // once shown, hold at least this long (no flash)
const DOM_SETTLE_MS = 1500; // how long to wait for the hero to appear in the DOM (lazy route chunk)

function pageHasFilm() {
  if (typeof window === "undefined") return false;
  const path = window.location.pathname.replace(/\/$/, "") || "/";
  const phone = window.matchMedia("(max-width: 767px)").matches;
  // Landing is locked per device (client, 2026-09-21): phones = film, desktop = photos.
  if (path === "/") return phone;
  // V1 (film heroes) is final everywhere (client, 2026-10-08) - the review toggle is gone.
  return path === "/events/ultimate-lap" || (phone && path === "/events/corporate-events");
}

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

/** The static shield in index.html (#gm-preloader) covers the time before this
 *  bundle runs. Remove it the moment the React loader is on screen, or the
 *  page is ready - whichever comes first. */
const removeStaticPreloader = () => { document.getElementById("gm-preloader")?.remove(); };

/** Resolves when one <img> has loaded AND decoded (or failed - a broken image must not trap the site). */
function imageSettled(img) {
  const decode = () => (typeof img.decode === "function" ? img.decode().catch(() => {}) : Promise.resolve());
  if (img.complete) return img.naturalWidth > 0 ? decode() : Promise.resolve();
  return new Promise((resolve) => {
    const done = () => { img.removeEventListener("load", done); img.removeEventListener("error", done); decode().then(resolve, resolve); };
    img.addEventListener("load", done, { once: true });
    img.addEventListener("error", done, { once: true });
  });
}

/** The images the brand moment depends on: everything VISIBLE inside the hero
 *  (the active slide - an inactive slide sits at opacity 0 and the rotation
 *  itself holds until the next frame has decoded, see Hero.jsx), plus any
 *  eager image whose box starts inside the first viewport. Waits briefly for
 *  the hero to mount (lazy route chunk) before taking the snapshot. */
async function whenCriticalMediaReady() {
  const t0 = performance.now();
  let hero = document.querySelector(".gm-hero, .mh, [data-gate-media]");
  while (!hero && performance.now() - t0 < DOM_SETTLE_MS) {
    await sleep(100);
    hero = document.querySelector(".gm-hero, .mh, [data-gate-media]");
  }
  const vh = window.innerHeight || 800;
  const imgs = Array.from(document.images).filter((img) => {
    if (img.loading === "lazy") return false;
    // A hero frame that is not on screen at reveal must not hold the site
    // (slow-line trace, 2026-10-22: with nothing lazy, the NEXT frame - the
    // heaviest file - lost the bandwidth race to the rest of the page and
    // held the shield for 20s while the visible frame had been ready for 15).
    const slide = img.closest(".gm-hero__slide");
    if (slide && !slide.classList.contains("is-active")) return false;
    if (hero && hero.contains(img)) return true;
    const r = img.getBoundingClientRect();
    return r.width > 0 && r.top < vh;
  });
  await Promise.all(imgs.map(imageSettled));
}

/** The hero wordmark -> nav lockup scrub (motion/reveals.js initHeroWordmark).
 *  body[data-wm-state] is "boot" from the hero's first paint until the scrub is
 *  built ("live" / "docked") or the static fallback is in place ("static").
 *  Resolves once it has left "boot"; at once on pages without a wordmark hero
 *  (no attribute within DOM_SETTLE_MS); never later than WM_CAP_MS. */
async function whenWordmarkReady() {
  const body = document.body;
  const state = () => body.getAttribute("data-wm-state");
  const t0 = performance.now();
  while (!state() && performance.now() - t0 < DOM_SETTLE_MS) await sleep(50);
  if (!state() || state() !== "boot" || window.__gmWmReady) return;
  await new Promise((resolve) => {
    let mo = null;
    const t = setTimeout(finish, WM_CAP_MS);
    function finish() { clearTimeout(t); mo?.disconnect(); window.removeEventListener(WM_READY, finish); resolve(); }
    window.addEventListener(WM_READY, finish);
    if ("MutationObserver" in window) {
      mo = new MutationObserver(() => { if (state() !== "boot") finish(); });
      mo.observe(body, { attributes: true, attributeFilter: ["data-wm-state"] });
    }
    if (state() !== "boot") finish();
  });
}

export default function Loader({ onDone }) {
  const [visible, setVisible] = useState(false);
  const doneRef = useRef(false);

  // Body scroll lock follows VISIBILITY, nothing else (client, 2026-09-19:
  // locking from inside the gating closures left `overflow: hidden` on <body>).
  useEffect(() => {
    if (!visible) return undefined;
    // Hand over from the static shield only once the React shield has its
    // image, so the swap is invisible (same size, same spot, same artwork).
    const img = document.querySelector(".gm-loader .gm-shine__mark");
    if (!img || img.complete) removeStaticPreloader();
    else { img.addEventListener("load", removeStaticPreloader, { once: true }); img.addEventListener("error", removeStaticPreloader, { once: true }); }
    lockScroll("loader");
    return () => { img?.removeEventListener("load", removeStaticPreloader); img?.removeEventListener("error", removeStaticPreloader); unlockScroll("loader"); };
  }, [visible]);

  useEffect(() => {
    let alive = true;

    // Resolves when a film hero reports playback, autoplay is REFUSED
    // (Safari, 2026-10-17: no point holding 12s for a frame that can only
    // arrive after a touch), or the cap.
    const filmPlaying = () => new Promise((resolve) => {
      const t = setTimeout(finish, FILM_CAP_MS);
      function finish() { clearTimeout(t); window.removeEventListener(FILM_PLAYING, finish); window.removeEventListener(FILM_BLOCKED, finish); resolve(); }
      window.addEventListener(FILM_PLAYING, finish);
      window.addEventListener(FILM_BLOCKED, finish);
    });

    const finishInitial = () => {
      if (!alive || doneRef.current) return;
      doneRef.current = true;
      removeStaticPreloader();
      setVisible(false);
      onDone?.();
      window.__gmLoaderDone = true;
      window.dispatchEvent(new CustomEvent(LOADER_DONE));
    };

    const film = pageHasFilm();
    let shownAt = 0;
    // Grace: only raise the shield if the page is NOT ready almost at once.
    const graceT = setTimeout(() => {
      if (!alive || doneRef.current) return;
      shownAt = performance.now();
      setVisible(true);
    }, GRACE_MS);

    const gates = [whenDisplayFontReady(), whenCriticalMediaReady(), whenWordmarkReady()];
    if (film) gates.push(filmPlaying());
    const cap = sleep(film ? FILM_CAP_MS + 500 : MEDIA_CAP_MS);
    Promise.race([Promise.all(gates), cap]).then(async () => {
      clearTimeout(graceT);
      if (shownAt) {
        const left = MIN_SHOW_MS - (performance.now() - shownAt);
        if (left > 0) await sleep(left);
      }
      finishInitial();
    });

    // Later: a film hero mounted by client-side navigation.
    let pendingCount = 0;
    const onPending = () => {
      if (!doneRef.current) return; // initial load already gating
      pendingCount += 1;
      setVisible(true);
      filmPlaying().then(() => {
        if (!alive) return;
        pendingCount = Math.max(0, pendingCount - 1);
        if (pendingCount === 0) setVisible(false);
      });
    };
    window.addEventListener(FILM_PENDING, onPending);

    return () => {
      alive = false;
      clearTimeout(graceT);
      window.removeEventListener(FILM_PENDING, onPending);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!visible) return null;
  return (
    <div className="gm-loader" data-testid="brand-loader">
      <ShineMark size={240} className="gm-loader__mark" testid="brand-loader-mark" />
    </div>
  );
}
